/**
 * useOfferEdit Hook
 * Manages state and actions for offer creation/editing
 * Role-aware for future Business Portal integration
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { offerService } from '../services/offerService';
import { 
  validateOfferForm, 
  calculateExpirationDate,
  calculateTimeRemaining,
  parseDurationFromDate
} from '../utils/offerValidation';

/**
 * Custom hook for offer editing logic
 * @param {Object} park - Park object
 * @param {Object} existingOffer - Existing offer (null for create)
 * @param {Object} options - Configuration options
 * @param {string} options.role - User role ('admin' or 'business')
 * @param {Function} options.onSuccess - Success callback
 * @returns {Object} - Offer editing interface
 */
const useOfferEdit = (park, existingOffer, { role = 'admin', onSuccess }) => {
  // Form state
  const [formData, setFormData] = useState({
    offer_title: '',
    offer_description: '',
    offer_terms: '',
    business_email: ''
  });
  
  const [offerDuration, setOfferDuration] = useState('1month');
  const [subscriptionMonths, setSubscriptionMonths] = useState(3);
  const [existingSubscription, setExistingSubscription] = useState(null);
  const [saving, setSaving] = useState(false);

  // Initialize form data from existing offer
  useEffect(() => {
    if (existingOffer) {
      setFormData({
        offer_title: existingOffer.offer_title || '',
        offer_description: existingOffer.offer_description || '',
        offer_terms: existingOffer.offer_terms || '',
        business_email: existingOffer.business_email || ''
      });

      // Parse duration from expiration date
      if (existingOffer.expiration_date) {
        const duration = parseDurationFromDate(existingOffer.expiration_date);
        setOfferDuration(duration);
      }
    } else if (park.business_email) {
      // Pre-fill business email if park has one
      setFormData(prev => ({
        ...prev,
        business_email: park.business_email
      }));
    }
  }, [existingOffer, park]);

  // Fetch existing subscription if editing and business email provided
  useEffect(() => {
    const fetchSubscription = async () => {
      if (park && (existingOffer?.business_email || park.business_email)) {
        try {
          const response = await offerService.getSubscription(park.id);
          if (response.data) {
            setExistingSubscription(response.data);
            setSubscriptionMonths(response.data.months_purchased || 3);
          }
        } catch (error) {
          // No subscription exists yet, that's ok
          setExistingSubscription(null);
        }
      }
    };

    fetchSubscription();
  }, [park, existingOffer]);

  /**
   * Handle form submission (create or update)
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = validateOfferForm(formData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const expiration_date = calculateExpirationDate(offerDuration);

      if (existingOffer) {
        // Update existing offer
        await offerService.updateOffer(
          existingOffer.id,
          {
            ...formData,
            expiration_date,
            subscription_months: parseInt(subscriptionMonths)
          },
          token
        );
        toast.success('Offer updated successfully!');
      } else {
        // Create new offer
        await offerService.createOffer(
          {
            park_id: park.id,
            category: park.category,
            ...formData,
            expiration_date,
            subscription_months: parseInt(subscriptionMonths)
          },
          token
        );
        toast.success('Offer created successfully!');
      }

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to save offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Update form field
   */
  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Return interface
  return {
    // State
    formData,
    offerDuration,
    subscriptionMonths,
    existingSubscription,
    saving,

    // Setters
    setFormData,
    setOfferDuration,
    setSubscriptionMonths,
    updateField,

    // Actions
    handleSubmit,

    // Utilities
    calculateExpirationDate,
    calculateTimeRemaining,

    // Metadata
    isEditing: !!existingOffer,
    role
  };
};

export default useOfferEdit;
